import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCartShopping, faBolt, faBan, faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';

import OrderCard from './OrderCard';
import {
    LinkButtons, UrgencyBadge, StatusPill,
    formatQty, sinceLabel, exactTime, daysSince,
} from './stockShared';

/**
 * The board: three stacks in the order work moves through them.
 *
 *   TO ORDER    — waiting on a manager. Carries the buying links, because the
 *                 whole job here is "see it, open the shop, mark it bought".
 *   ON THE WAY  — ordered, not yet received. Carries what the supplier said
 *                 about timing and how long it has actually been.
 *   CLOSED      — delivered or cancelled, collapsed by default. Present so the
 *                 question "did that ever arrive?" has an answer, absent from
 *                 the working view so it never competes with live work.
 *
 * A ROW SHOWS WHAT ITS READER CAN DO. The warehouse floor gets the item, the
 * urgency, when it is expected and one button — no links, no prices, no order
 * references, no invoices, none of which they can act on. Managers get the
 * buying half. Hiding rather than disabling: a button someone can never press
 * is just an unanswered question.
 */
export default function StockRequests({
    requests, orders, isManager,
    onOrder, onUndoLine, onCancel, onCancelOrder, onEdit, onInvoice,
    onReceiveOrder, onExpense, onGoCatalog,
}) {
    const [selected, setSelected] = useState([]);   // ids, "to order" only
    /* Ten at a time. The history is unbounded and nobody reads past the last
       few, but it must still be reachable — so it is a section like any other,
       not a collapsed drawer, and it grows on demand. */
    const [historyShown, setHistoryShown] = useState(10);

    /* NO CATEGORY FILTER HERE. The board is a to-do list, not a catalogue: it
       is short by nature, every line on it needs doing, and filtering a to-do
       list mostly hides work. Each row still carries its category as a label.
       Filtering belongs on the stock list, where there are 48 items. */
    const toOrder = requests.filter((r) => r.status === 'requested');

    /* ORDERS, NOT LINES, once something has been bought. A purchase is the
       thing that has an expected date, an invoice and a delivery — see
       OrderCard. Cancelled requests never joined an order, so they are listed
       separately under the closed section. */
    const live = (orders || []).filter((o) => o.state === 'open' || o.state === 'part-received');
    const settled = (orders || []).filter((o) => o.state === 'received' || o.state === 'cancelled');
    const cancelledRequests = requests.filter((r) => r.status === 'cancelled' && !r.order_id);

    /* One list, newest first — an order and a cancelled request are both
       "something that happened", and splitting them into two piles makes the
       reader check two places for the same question. */
    const history = [
        ...settled.map((o) => ({ kind: 'order', at: o.ordered_at, row: o })),
        ...cancelledRequests.map((r) => ({ kind: 'request', at: r.cancelled_at, row: r })),
    ].sort((a, b) => new Date(b.at) - new Date(a.at));
    const closedCount = history.length;

    const toggle = (id) => setSelected((s) => (
        s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
    ));
    const clearSelection = () => setSelected([]);

    const selectedRequests = toOrder.filter((r) => selected.includes(r.id));

    return (
        <div className="stk__board">
            {/* ── To order ─────────────────────────────────────────────── */}
            <section className="stk__section">
                <div className="stk__sectionHead">
                    <h3 className="stk__sectionTitle">
                        {isManager ? 'To order' : 'Waiting to be ordered'}
                        <span className="stk__sectionCount">{toOrder.length}</span>
                    </h3>
                    {isManager && toOrder.length > 1 && (
                        <button
                            type="button"
                            className="stk__linkish"
                            onClick={() => setSelected(
                                selected.length === toOrder.length ? [] : toOrder.map((r) => r.id)
                            )}
                        >
                            {selected.length === toOrder.length ? 'Clear selection' : 'Select all'}
                        </button>
                    )}
                </div>

                {toOrder.length === 0 ? (
                    <EmptyState
                        title="Nothing waiting to be ordered"
                        body={isManager
                            ? 'When the warehouse runs low on something, it shows up here.'
                            : 'Open the stock list and tap Request on whatever is running out.'}
                        action={isManager ? null : { label: 'Open the stock list', onClick: onGoCatalog }}
                    />
                ) : (
                    <div className="stk__rows">
                        {toOrder.map((r) => (
                            <ToOrderRow
                                key={r.id}
                                request={r}
                                isManager={isManager}
                                selected={selected.includes(r.id)}
                                onToggle={() => toggle(r.id)}
                                onOrder={() => onOrder([r])}
                                onEdit={() => onEdit(r)}
                                onCancel={() => onCancel(r)}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* ── On the way ───────────────────────────────────────────── */}
            <section className="stk__section">
                <div className="stk__sectionHead">
                    <h3 className="stk__sectionTitle">
                        On the way
                        <span className="stk__sectionCount">{live.length}</span>
                    </h3>
                </div>

                {live.length === 0 ? (
                    <EmptyState
                        title="Nothing on order"
                        body={isManager
                            ? 'Once you mark requests ordered they become one order here, and wait until the warehouse confirms it arrived.'
                            : 'Anything the office has ordered will wait here until you confirm it arrived.'}
                    />
                ) : (
                    <div className="stk__rows">
                        {live.map((o) => (
                            <OrderCard
                                key={o.id}
                                order={o}
                                isManager={isManager}
                                onReceiveOrder={() => onReceiveOrder(o)}
                                onUndoLine={(line) => onUndoLine({ ...line, id: line.request_id })}
                                onInvoice={() => onInvoice(o)}
                                onExpense={() => onExpense(o)}
                                onCancelOrder={() => onCancelOrder(o)}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* ── Recently ordered ─────────────────────────────────────── */}
            <section className="stk__section">
                <div className="stk__sectionHead">
                    <h3 className="stk__sectionTitle">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                        Recently ordered
                        <span className="stk__sectionCount">{closedCount}</span>
                    </h3>
                </div>

                {closedCount === 0 ? (
                    <EmptyState
                        title="Nothing here yet"
                        body="Orders that have been received, and requests that were cancelled, are kept here."
                    />
                ) : (
                    <>
                        <div className="stk__rows stk__rows--quiet">
                            {history.slice(0, historyShown).map((entry) => (
                                entry.kind === 'order' ? (
                                    <OrderCard
                                        key={`o${entry.row.id}`}
                                        order={entry.row}
                                        isManager={isManager}
                                        onReceiveOrder={() => onReceiveOrder(entry.row)}
                                                onUndoLine={(line) => onUndoLine({ ...line, id: line.request_id })}
                                        onInvoice={() => onInvoice(entry.row)}
                                        onExpense={() => onExpense(entry.row)}
                                        onCancelOrder={() => onCancelOrder(entry.row)}
                                    />
                                ) : (
                                    <ClosedRow key={`r${entry.row.id}`} request={entry.row} />
                                )
                            ))}
                        </div>

                        {historyShown < history.length && (
                            <button
                                type="button"
                                className="stk__loadMore"
                                onClick={() => setHistoryShown((n) => n + 10)}
                            >
                                Load 10 more
                                <span className="stk__loadMoreRest">
                                    {history.length - historyShown} older
                                </span>
                            </button>
                        )}
                    </>
                )}
            </section>

            {/* Sticky bulk bar — one supplier run, several lines. */}
            {isManager && selectedRequests.length > 0 && (
                <div className="stk__bulkBar">
                    <span className="stk__bulkCount">
                        {selectedRequests.length} selected
                    </span>
                    <span className="stk__bulkNames">
                        {selectedRequests.slice(0, 3).map((r) => r.item_name).join(', ')}
                        {selectedRequests.length > 3 ? ` +${selectedRequests.length - 3} more` : ''}
                    </span>
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={clearSelection}>
                        Clear
                    </button>
                    <button
                        type="button"
                        className="stk__btn stk__btn--primary"
                        onClick={() => { onOrder(selectedRequests); clearSelection(); }}
                    >
                        <FontAwesomeIcon icon={faCartShopping} /> Mark ordered
                    </button>
                </div>
            )}

            <p className="stk__boardFoot">
                Need something that is not on this board?{' '}
                <button type="button" className="stk__linkish" onClick={onGoCatalog}>
                    {isManager ? 'Open the catalogue' : 'Open the stock list'}
                </button>
                {isManager ? ' to add or request it.' : ' and tap Request.'}
            </p>
        </div>
    );
}

/* ── Rows ─────────────────────────────────────────────────────────────────── */

/** "12 pack of 24 × Toilet rolls", or just the name before anyone decided. */
function RowTitle({ request: r }) {
    return (
        <h4 className="stk__rowTitle">
            {r.quantity ? (
                <>
                    <span className="stk__qty">{formatQty(r.quantity)}</span>
                    <span className="stk__times">×</span>
                </>
            ) : null}
            {r.item_name}
        </h4>
    );
}

function ToOrderRow({ request: r, isManager, selected, onToggle, onOrder, onEdit, onCancel }) {
    // Three days unbought is the point at which a warehouse starts improvising,
    // so the row says so rather than leaving it to be noticed.
    const stale = daysSince(r.requested_at) >= 3;

    return (
        <article className={`stk__row ${r.urgency === 'urgent' ? 'is-urgent' : ''} ${selected ? 'is-selected' : ''}`}>
            {isManager && (
                <label className="stk__rowPick">
                    <input type="checkbox" checked={selected} onChange={onToggle} aria-label={`Select ${r.item_name}`} />
                </label>
            )}

            <div className="stk__rowMain">
                {/* Category first, then the status/urgency badge — the shelf is
                    what you look for, the state is what you check after. */}
                <div className="stk__rowTop">
                    <RowTitle request={r} />
                    <span className="stk__cat">{r.category_name}</span>
                    <UrgencyBadge urgency={r.urgency} />
                </div>

                <p className="stk__rowMeta">
                    {isManager ? <>Requested by {r.requested_by_name || 'a colleague'}{' · '}</> : <>Asked for{' '}</>}
                    <span className={stale ? 'stk__stale' : ''} title={exactTime(r.requested_at)}>
                        {sinceLabel(r.requested_at)}
                    </span>
                    {isManager && r.item_delivery_estimate
                        ? <> · delivery usually {r.item_delivery_estimate}</>
                        : null}
                </p>

                {isManager && <LinkButtons links={r.links} />}
            </div>

            <div className="stk__rowActions">
                {isManager ? (
                    <>
                        <button type="button" className="stk__btn stk__btn--primary" onClick={onOrder}>
                            <FontAwesomeIcon icon={faCartShopping} /> Mark ordered
                        </button>
                        <div className="stk__rowActionsMinor">
                            <button type="button" className="stk__iconBtn" onClick={onEdit} title="Change how urgent this is">
                                <FontAwesomeIcon icon={faBolt} /> Urgency
                            </button>
                            <button type="button" className="stk__iconBtn stk__iconBtn--danger" onClick={onCancel} title="Cancel this request">
                                <FontAwesomeIcon icon={faBan} /> Cancel
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <span className="stk__waiting">The office has been told</span>
                        {r.urgency !== 'urgent' && (
                            <button type="button" className="stk__iconBtn" onClick={onEdit}>
                                <FontAwesomeIcon icon={faBolt} /> Mark urgent
                            </button>
                        )}
                    </>
                )}
            </div>
        </article>
    );
}

/** A request cancelled before it ever joined an order. */
function ClosedRow({ request: r }) {
    return (
        <article className="stk__row stk__row--closed">
            <div className="stk__rowMain">
                <div className="stk__rowTop">
                    <RowTitle request={r} />
                    <span className="stk__cat">{r.category_name}</span>
                    <StatusPill status="cancelled" />
                </div>
                <p className="stk__rowMeta">
                    Cancelled by {r.cancelled_by_name || 'a manager'} ·{' '}
                    <span title={exactTime(r.cancelled_at)}>{sinceLabel(r.cancelled_at)}</span>
                </p>
                {r.cancel_reason ? <p className="stk__rowNote">“{r.cancel_reason}”</p> : null}
            </div>
        </article>
    );
}

function EmptyState({ title, body, action }) {
    return (
        <div className="stk__empty">
            <p className="stk__emptyTitle">{title}</p>
            <p className="stk__emptyBody">{body}</p>
            {action ? (
                <button type="button" className="stk__btn stk__btn--primary" onClick={action.onClick}>
                    {action.icon ? <FontAwesomeIcon icon={action.icon} /> : null} {action.label}
                </button>
            ) : null}
        </div>
    );
}
